import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import { FiPlus, FiChevronDown, FiX } from 'react-icons/fi';

export default function LookupSelect({ table, label, value, onChange, onSelectItem, placeholder, required, filters = {}, createExtras = {}, disabled }) {
  const { tenant } = useAuth();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const filtersKey = JSON.stringify(filters);

  const fetchOptions = async () => {
    setLoading(true);
    try {
      const data = await api.getLookups(table, tenant.id, filters);
      setOptions(data);
    } catch (err) {
      console.error(`Failed to fetch ${table}:`, err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tenant) fetchOptions();
  }, [tenant, table, filtersKey]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch('');
        setHighlightIdx(-1);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  const handleAdd = async () => {
    if (!newValue.trim()) return;
    // Name already exists: select it rather than creating a duplicate.
    const existing = options.find(o => (o.name || '').trim().toLowerCase() === newValue.trim().toLowerCase());
    if (existing) {
      onChange(existing.id);
      onSelectItem?.(existing);
      setIsAdding(false);
      setNewValue('');
      return;
    }
    if (!tenant) {
      alert('No tenant associated with your account. Please contact an administrator.');
      return;
    }
    try {
      const created = await api.createLookup(table, tenant.id, newValue.trim(), createExtras);
      setOptions([...options, created].sort((a, b) => a.name.localeCompare(b.name)));
      onChange(created.id);
      onSelectItem?.(created);
      setIsAdding(false);
      setNewValue('');
    } catch (err) {
      console.error(`Error creating ${label}:`, err);
      alert(`Error creating ${label}: ${err.message || 'Unknown error'}`);
    }
  };

  const selectedOption = options.find(o => o.id === value);
  const filtered = search
    ? options.filter(o => o.name.toLowerCase().includes(search.toLowerCase()))
    : options;

  const selectItem = useCallback((opt) => {
    onChange(opt.id);
    onSelectItem?.(opt);
    setIsOpen(false);
    setSearch('');
    setHighlightIdx(-1);
  }, [onChange, onSelectItem]);

  const clearSelection = () => {
    onChange('');
    onSelectItem?.(null);
    setSearch('');
  };

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightIdx < 0 || !listRef.current) return;
    const items = listRef.current.querySelectorAll('[data-option]');
    if (items[highlightIdx]) {
      items[highlightIdx].scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIdx]);

  // Reset highlight when filtered list changes
  useEffect(() => {
    setHighlightIdx(-1);
  }, [search]);

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setIsOpen(true);
        setSearch('');
        setHighlightIdx(0);
        return;
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightIdx(prev => (prev < filtered.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightIdx(prev => (prev > 0 ? prev - 1 : filtered.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightIdx >= 0 && filtered[highlightIdx]) {
          selectItem(filtered[highlightIdx]);
        } else if (filtered.length === 1) {
          selectItem(filtered[0]);
        }
        break;
      case 'Tab':
        // Select the highlighted item (or first match) before tabbing away
        if (highlightIdx >= 0 && filtered[highlightIdx]) {
          selectItem(filtered[highlightIdx]);
        } else if (filtered.length === 1) {
          selectItem(filtered[0]);
        } else {
          setIsOpen(false);
          setSearch('');
          setHighlightIdx(-1);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearch('');
        setHighlightIdx(-1);
        break;
    }
  };

  // ── Add-new inline row ──
  if (isAdding) {
    return (
      <div className="w-full">
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          {label}
        </label>
        <div className="flex gap-2">
          <input
            autoFocus
            className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 dark:bg-slate-900 dark:border-slate-700 dark:text-white"
            placeholder={`New ${label}...`}
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } if (e.key === 'Escape') { setIsAdding(false); setNewValue(''); } }}
          />
          <button type="button" onClick={handleAdd} className="bg-primary-600 text-white px-3 rounded-md text-sm">Add</button>
          <button type="button" onClick={() => { setIsAdding(false); setNewValue(''); }} className="bg-slate-200 text-slate-700 px-3 rounded-md text-sm dark:bg-slate-800 dark:text-slate-300">Cancel</button>
        </div>
        {/* Keep native required validation while adding: text typed here isn't
            a selection until Add is clicked. */}
        {required && <input type="text" required value={value || ''} onChange={() => {}} tabIndex={-1} className="sr-only" aria-hidden="true" />}
      </div>
    );
  }

  // ── Combobox with keyboard navigation ──
  return (
    <div className="w-full" ref={containerRef}>
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
        {label}
      </label>
      <div className="flex gap-1.5">
        <div className="relative flex-1">
          <input
            ref={inputRef}
            type="text"
            disabled={disabled}
            placeholder={placeholder || `Search ${label}...`}
            value={isOpen ? search : (selectedOption?.name || '')}
            onFocus={() => { setIsOpen(true); setSearch(''); setHighlightIdx(-1); }}
            onChange={(e) => { setSearch(e.target.value); if (!isOpen) setIsOpen(true); }}
            onKeyDown={handleKeyDown}
            role="combobox"
            aria-expanded={isOpen}
            aria-autocomplete="list"
            aria-activedescendant={highlightIdx >= 0 && filtered[highlightIdx] ? `opt-${filtered[highlightIdx].id}` : undefined}
            className="flex h-10 w-full rounded-md border border-slate-300 bg-white pl-3 pr-16 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:outline-none dark:bg-slate-900 dark:border-slate-700 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <div className="absolute inset-y-0 right-0 flex items-center gap-0.5 pr-2">
            {value && (
              <button type="button" tabIndex={-1} onClick={(e) => { e.stopPropagation(); clearSelection(); inputRef.current?.focus(); }} className="p-0.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
                <SafeIcon icon={FiX} className="h-3.5 w-3.5" />
              </button>
            )}
            <SafeIcon icon={FiChevronDown} className={`h-4 w-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </div>

          {isOpen && (
            <ul ref={listRef} role="listbox" className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-slate-200 bg-white py-1 text-sm shadow-lg dark:bg-slate-900 dark:border-slate-700">
              {filtered.length === 0 && (
                <li className="px-3 py-2 text-slate-400">No matches</li>
              )}
              {filtered.map((opt, idx) => (
                <li
                  key={opt.id}
                  id={`opt-${opt.id}`}
                  data-option
                  role="option"
                  aria-selected={opt.id === value}
                  onClick={() => selectItem(opt)}
                  onMouseEnter={() => setHighlightIdx(idx)}
                  className={`cursor-pointer px-3 py-2 ${
                    idx === highlightIdx
                      ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-800 dark:text-primary-300'
                      : opt.id === value
                        ? 'bg-primary-50 dark:bg-primary-900/30 font-medium text-primary-700 dark:text-primary-400'
                        : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800'
                  }`}
                >
                  {opt.name}
                </li>
              ))}
            </ul>
          )}
        </div>
        <button
          type="button"
          onClick={() => setIsAdding(true)}
          disabled={disabled}
          tabIndex={-1}
          title={`Add new ${label}`}
          className="flex items-center justify-center h-10 w-10 shrink-0 rounded-md border border-slate-300 bg-white text-slate-600 hover:bg-primary-50 hover:text-primary-600 hover:border-primary-300 transition-colors dark:bg-slate-900 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-primary-900/30 dark:hover:text-primary-400 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <SafeIcon icon={FiPlus} className="h-4 w-4" />
        </button>
      </div>
      {/* Hidden input for native form validation */}
      {required && <input type="text" required value={value || ''} onChange={() => {}} tabIndex={-1} className="sr-only" aria-hidden="true" />}
    </div>
  );
}
